import React from 'react';
import { Calendar, Clock, Droplets, Moon } from 'lucide-react';

/**
 * Компонент для ввода основных параметров менструального цикла
 */
const CycleParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  cycleTypes 
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Дата начала последней менструации */}
      <div>
        <label htmlFor="lastPeriodDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-rose-500" />
          Дата начала последней менструации
        </label>
        <input
          type="date"
          id="lastPeriodDate"
          name="lastPeriodDate"
          value={formData.lastPeriodDate}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.lastPeriodDate ? 'input-error' : ''}`}
          max={new Date().toISOString().split('T')[0]} // Не позволяет выбрать будущую дату
        />
        {errors.lastPeriodDate && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.lastPeriodDate}
          </p>
        )}
      </div>

      {/* Длительность цикла */}
      <div>
        <label htmlFor="cycleLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Clock className="w-4 h-4 mr-2 text-rose-500" />
          Длительность цикла (дней)
        </label>
        <div className="flex">
          <input
            type="number"
            id="cycleLength"
            name="cycleLength"
            value={formData.cycleLength}
            onChange={handleInputChange}
            min="21"
            max="45"
            className={`form-input w-full ${errors.cycleLength ? 'input-error' : ''}`}
            placeholder="Обычно 28 дней"
          />
          <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 dark:bg-gray-600 dark:text-gray-300 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md">
            дней
          </span>
        </div>
        {errors.cycleLength ? (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.cycleLength}
          </p>
        ) : (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Средняя длительность - 28 дней (от первого дня менструации до следующей)
          </p>
        )}
      </div>

      {/* Длительность менструации */}
      <div>
        <label htmlFor="periodLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Droplets className="w-4 h-4 mr-2 text-rose-500" />
          Длительность менструации
        </label>
        <div className="flex">
          <input
            type="number"
            id="periodLength"
            name="periodLength"
            value={formData.periodLength}
            onChange={handleInputChange}
            min="1"
            max="10"
            className={`form-input w-full ${errors.periodLength ? 'input-error' : ''}`}
            placeholder="Обычно 3-7 дней"
          />
          <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 dark:bg-gray-600 dark:text-gray-300 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md">
            дней
          </span>
        </div>
        {errors.periodLength ? (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.periodLength}
          </p>
        ) : (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Обычно длится от 3 до 7 дней
          </p>
        )}
      </div>

      {/* Длительность лютеиновой фазы */}
      <div>
        <label htmlFor="lutealPhaseLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Moon className="w-4 h-4 mr-2 text-rose-500" />
          Длительность лютеиновой фазы
        </label>
        <div className="flex">
          <input
            type="number"
            id="lutealPhaseLength"
            name="lutealPhaseLength"
            value={formData.lutealPhaseLength}
            onChange={handleInputChange}
            min="10"
            max="16"
            className={`form-input w-full ${errors.lutealPhaseLength ? 'input-error' : ''}`}
            placeholder="Обычно 14 дней"
          />
          <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 dark:bg-gray-600 dark:text-gray-300 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md">
            дней
          </span>
        </div>
        {errors.lutealPhaseLength ? (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.lutealPhaseLength}
          </p>
        ) : (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Фаза от овуляции до начала менструации, обычно 12-16 дней
          </p>
        )}
      </div>

      {/* Тип цикла */}
      <div className="col-span-full bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600 mt-2">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Особенности цикла (опционально)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {cycleTypes.map((type) => (
            <label 
              key={type.id} 
              className={`flex items-center p-3 rounded-lg cursor-pointer transition ${
                formData.cycleType === type.id 
                  ? 'bg-rose-100 dark:bg-rose-900/30 border border-rose-300 dark:border-rose-700' 
                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <input
                type="radio"
                name="cycleType"
                value={type.id}
                checked={formData.cycleType === type.id}
                onChange={handleInputChange}
                className="form-radio mr-2"
              />
              <div>
                <span className="text-sm font-medium block text-gray-700 dark:text-gray-300">
                  {type.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden md:block">
                  {type.description}
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CycleParametersSection;